import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

// Map section name → route
const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const CertificationsForm = () => {
  const [certification, setCertification] = useState({ name: "", provider: "", year: "" });
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  useEffect(() => {
    const saved = localStorage.getItem("certificationsList");
    if (saved) setSavedCertifications(JSON.parse(saved));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCertification({ ...certification, [name]: value });
  };

  const handleSaveAndNext = () => {
    // Save certification if all fields are filled
    if (certification.name.trim() && certification.provider.trim() && certification.year.trim()) {
      const updatedList =
        editIndex !== null
          ? savedCertifications.map((c, i) => (i === editIndex ? certification : c))
          : [...savedCertifications, certification];

      setSavedCertifications(updatedList);
      localStorage.setItem("certificationsList", JSON.stringify(updatedList));
      setCertification({ name: "", provider: "", year: "" });
      setEditIndex(null);
    }

    // Navigate to next section
    const currentSection = "Certifications";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], { state: { selectedTemplate, selectedTheme } });
    } else {
      navigate("/mainpage", { state: { selectedTemplate, selectedTheme } });
    }
  };

  const handleEdit = (index) => {
    setCertification(savedCertifications[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updatedList = savedCertifications.filter((_, i) => i !== index);
    setSavedCertifications(updatedList);
    localStorage.setItem("certificationsList", JSON.stringify(updatedList));
  };

  return (
    <Box display="flex" bgcolor="#EFF7FF" minHeight="100vh">
      <Box flex={1}>
        {/* Header / AppBar */}
        <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
              Certifications
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: 5 }}>
          <Grid container spacing={4}>
            {/* Form */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  bgcolor: "white",
                  border: "1px solid #B3D9F7",
                  boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: "#002B5B" }}
                >
                  Add Certification
                </Typography>
                <Divider sx={{ mb: 3, borderColor: "#0288D1", borderWidth: 2 }} />

                <TextField
                  label="Certification Name"
                  name="name"
                  fullWidth
                  value={certification.name}
                  onChange={handleChange}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#B3D9F7" },
                      "&:hover fieldset": { borderColor: "#0288D1" },
                      "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                  }}
                />
                <TextField
                  label="Provider"
                  name="provider"
                  fullWidth
                  value={certification.provider}
                  onChange={handleChange}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#B3D9F7" },
                      "&:hover fieldset": { borderColor: "#0288D1" },
                      "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                  }}
                />
                <TextField
                  label="Year"
                  name="year"
                  type="year"
                  fullWidth
                  value={certification.year}
                  onChange={handleChange}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#B3D9F7" },
                      "&:hover fieldset": { borderColor: "#0288D1" },
                      "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                      borderRadius: 2,
                      py: 1.2,
                      px: 4,
                      fontWeight: "bold",
                      backgroundColor: "#002B5B",
                      "&:hover": { backgroundColor: "#0288D1" },
                      boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
                    }}
                    onClick={handleSaveAndNext}
                  >
                    Save & Next
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Display List */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  bgcolor: "white",
                  border: "1px solid #B3D9F7",
                  boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: "bold", color: "#002B5B" }}
                >
                  Saved Certifications
                </Typography>
                <Divider sx={{ mb: 3, borderColor: "#0288D1", borderWidth: 2 }} />

                {savedCertifications.length > 0 ? (
                  savedCertifications.map((cert, index) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 2,
                        boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                        border: "1px solid #B3D9F7",
                        borderLeft: "4px solid #002B5B",
                        borderRadius: 2,
                        "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ color: "#002B5B", fontWeight: "bold" }}>
                          {cert.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#0288D1", mt: 0.5 }}>
                          {cert.provider} • Year: {cert.year}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <IconButton
                          onClick={() => handleEdit(index)}
                          sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ mt: 2, textAlign: "center", color: "#0288D1" }}
                  >
                    No certifications added yet.
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default CertificationsForm;